import type { SeriesTarget } from '../types'

export const EMPTY_SERIES_TARGET: SeriesTarget = {
  identifiers: [],
  accountIds: [],
}

// Looks up the rows a series/category targets, falling back to the name with
// its " (NN.NN%)" piechart suffix stripped (see GraphLogic#adjusted_columns).
// Returns an empty target when the name references nothing, or when the map is
// absent (e.g. a graph payload cached before seriesTargets existed).
export function lookupSeriesTarget(
  seriesTargets: Record<string, SeriesTarget> | undefined,
  candidate: string | undefined
): SeriesTarget {
  if (!candidate || !seriesTargets) return EMPTY_SERIES_TARGET
  if (candidate in seriesTargets) return seriesTargets[candidate]
  const suffixStart = candidate.lastIndexOf(' (')
  if (suffixStart > 0) {
    const stripped = candidate.slice(0, suffixStart)
    if (stripped in seriesTargets) return seriesTargets[stripped]
  }
  return EMPTY_SERIES_TARGET
}

// True when the target resolves to at least one report row — i.e. clicking it
// would highlight something, so it should read as interactive (pointer cursor).
export function isSeriesTargetClickable(target: SeriesTarget): boolean {
  return target.identifiers.length > 0 || target.accountIds.length > 0
}

// The echarts cursor for a series/category: a pointer when its metric resolves
// to a report row (so it reads as clickable), the default cursor otherwise.
export function seriesCursor(
  seriesTargets: Record<string, SeriesTarget> | undefined,
  candidate: string | undefined
): 'pointer' | 'default' {
  return isSeriesTargetClickable(lookupSeriesTarget(seriesTargets, candidate))
    ? 'pointer'
    : 'default'
}
